import { Inject, Injectable } from '@angular/core';
import { LinkRegistryService } from '../link-registry.service';
import { SecurityService } from '../security.service';
import { AppRouter, ROUTER } from './app-router.interface';

export interface InitialState {
  readonly linkId: string;
  readonly url: string;
  readonly name: string;
}

/**
 * InitialStateService
 *
 * Provides initial page for the application.
 *
 * Consumer app must initialize this service current preference value
 * and a list of ordered linkIds, from descending order of priority
 *
 * This service will then check for permission for each page and assemble
 * a final list of pages which can be selected as initial page
 */
@Injectable({
  providedIn: 'root'
})
export class InitialStateService {
  private initialState: InitialState;
  private fallBackState: InitialState;
  private selectableStates: Array<InitialState> = [];

  constructor(@Inject(SecurityService) private securityService: SecurityService,
              @Inject(LinkRegistryService) private linkRegistryService: LinkRegistryService,
              @Inject(ROUTER) private appRouter: AppRouter) {
  }

  /**
   * Initializes router's initial and fallback state based on user preferences.
   *
   * @param linkIds Links to the stated which could be used as initial states
   * @param fallBackLinkId linkId which will be used if user has no permissions to navigate to any of the link ids
   * @param preferredLinkId  Link to the state selected by user as an initial state
   */
  init(linkIds: Array<string>, fallBackLinkId: string, preferredLinkId: string): void {
    this.selectableStates = this.resolveAndSortStates(linkIds, preferredLinkId);
    this.fallBackState = this.resolveState(fallBackLinkId);
    this.initialState = this.selectableStates.length > 0 ? this.selectableStates[0] : this.fallBackState;
    if (this.initialState  !== undefined) {
      this.appRouter.setInitialState(this.initialState.url);
    }
    if (this.fallBackState !== undefined && this.fallBackState.url !== undefined) {
      this.appRouter.setFallbackState(this.fallBackState.url);
    }
  }

  /**
   * Returns initial state for the application which will be navigated to if user loads application without specifying
   * any url.
   *
   * @return InitialState containing state name, linkId and url
   */
  getInitialState(): InitialState {
    return this.initialState;
  }

  /**
   * Returns fallback state which will be used when user has no permissions to navigate  none of the configured default states.
   * Most likely it will be a 404 error page
   *
   * @return
   */
  getFallBackState(): InitialState {
    return this.fallBackState;
  }

  /**
   * Retrieves all states that can be selected by user as a starting page
   *
   * @return
   */
  getSelectableStates(): Array<InitialState> {
    return this.selectableStates;
  }

  private resolveAndSortStates(linkIds: Array<string>, preferredLinkId: string): Array<InitialState> {
    const result: Array<InitialState> = [];
    linkIds
      .map((linkId: string): InitialState => this.resolveState(linkId))
      .forEach((state: InitialState): void => {
        if (state !== undefined) {
          if (preferredLinkId === state.linkId) {
            result.unshift(state);
          } else {
            result.push(state);
          }
        }
      });
    return result;
  }

  /**
   * Reverse lookup on all states based on the given linkId
   *
   * @param linkId
   * @returns
   */
  private resolveState(linkId: string): InitialState {
    let url = this.linkRegistryService.getLink(linkId);
    if (url !== undefined) {
      const queryStartOrEnd = url.indexOf('?') > 0 ? url.indexOf('?') : undefined;
      url = url.substring(url.indexOf('#') + 1, queryStartOrEnd);
      if (this.securityService.isAuthorizedUrl(url)) {
        const state = this.appRouter.getStateFromUrl<{ name: string }>(url);
        if (state !== undefined) {
          return { url,  linkId, name: state?.name };
        }
      }
    }
  }
}
